import {CdkMenu, CdkMenuItem, CdkMenuTrigger} from "@angular/cdk/menu"
import {ConnectedPosition} from "@angular/cdk/overlay"
import {ChangeDetectionStrategy, Component, computed, input, output, signal} from "@angular/core"
import {
    AssetActionsMenuComponent,
    AssetCellChipComponent,
    AssetDimensionOverlayComponent,
    AssetDownloadMenuComponent,
    AssetGridCellComponent,
    AssetMenuAction,
} from "@common/components/asset-grid"
import {LoadingSpinnerIconComponent} from "@common/components/progress/loading-spinner-icon/loading-spinner-icon.component"
import {JobState, MaterialOutputType} from "@generated"
import {FlatThumbnailOptionLabels} from "@labels"
import type {MaterialAssetGridOutputFragment} from "@platform/components/materials/material-asset-grid/material-asset-grid/material-asset-grid.generated"

type ThumbnailResult = NonNullable<MaterialAssetGridOutputFragment["result"]>

type ThumbnailCellState =
    | {kind: "empty"; selectedType: MaterialOutputType}
    | {kind: "loading"; selectedType: MaterialOutputType}
    | {kind: "failed"; selectedType: MaterialOutputType}
    | {kind: "complete"; selectedType: MaterialOutputType; result: ThumbnailResult}

type SizeEntry = {
    type: MaterialOutputType
    label: string
    size: number
    isSelected: boolean
    isRendered: boolean
    isLoading: boolean
    result: ThumbnailResult | null
}

const DEFAULT_TYPE = MaterialOutputType.Thumbnail9x9inches

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-thumbnail-asset-cell",
    templateUrl: "./thumbnail-asset-cell.component.html",
    styleUrls: ["./thumbnail-asset-cell.component.scss"],
    imports: [
        AssetActionsMenuComponent,
        AssetCellChipComponent,
        AssetDimensionOverlayComponent,
        AssetDownloadMenuComponent,
        AssetGridCellComponent,
        CdkMenu,
        CdkMenuItem,
        CdkMenuTrigger,
        LoadingSpinnerIconComponent,
    ],
})
export class ThumbnailAssetCellComponent {
    readonly $outputs = input<readonly MaterialAssetGridOutputFragment[]>([], {alias: "outputs"})
    readonly $pendingTypes = input<ReadonlySet<MaterialOutputType>>(new Set(), {alias: "pendingTypes"})
    readonly $hasCyclesMaterial = input<boolean>(false, {alias: "hasCyclesMaterial"})
    readonly $canEdit = input<boolean>(false, {alias: "canEdit"})
    readonly $canDownload = input<boolean>(false, {alias: "canDownload"})

    readonly render = output<MaterialOutputType>()
    readonly rerender = output<MaterialOutputType>()
    readonly remove = output<MaterialOutputType>()
    readonly cancel = output<MaterialOutputType>()
    readonly retry = output<MaterialOutputType>()
    readonly preview = output<void>()

    private readonly $renderedOutputsByType = computed(() => {
        const map = new Map<MaterialOutputType, MaterialAssetGridOutputFragment>()
        for (const output of this.$outputs()) {
            if (output.result) map.set(output.type, output)
        }
        return map
    })

    private readonly $userSelectedType = signal<MaterialOutputType | null>(null)

    readonly $selectedType = computed<MaterialOutputType>(() => {
        const user = this.$userSelectedType()
        if (user) return user
        const rendered = this.$renderedOutputsByType()
        const sorted = sortedSizes()
        const smallest = sorted.find((s) => rendered.has(s.type))
        return smallest?.type ?? DEFAULT_TYPE
    })

    // A material can carry several outputs of the same type (one per revision).
    // Pick a single canonical output per type (last wins) and use it everywhere so
    // the top-right chip and the dropdown row never disagree on a size's state.
    private readonly $outputByType = computed(() => new Map(this.$outputs().map((o) => [o.type, o])))

    readonly $selectedOutput = computed(() => this.$outputByType().get(this.$selectedType()))
    readonly $selectedPending = computed(() => this.$pendingTypes().has(this.$selectedType()))

    readonly $state = computed<ThumbnailCellState>(() => {
        const selectedType = this.$selectedType()
        const output = this.$selectedOutput()
        const jobState = output?.job?.state
        if (this.$selectedPending() || (jobState && isJobRunning(jobState))) return {kind: "loading", selectedType}
        if (!output) return {kind: "empty", selectedType}
        if (jobState === JobState.Failed || jobState === JobState.Cancelled) return {kind: "failed", selectedType}
        if (output.result) return {kind: "complete", selectedType, result: output.result}
        return {kind: "empty", selectedType}
    })

    readonly $sizes = computed<readonly SizeEntry[]>(() => {
        const selectedType = this.$selectedType()
        const pendingTypes = this.$pendingTypes()
        const outputsByType = this.$outputByType()
        return sortedSizes().map(({type, size}) => {
            const output = outputsByType.get(type)
            const result = output?.result ?? null
            const jobState = output?.job?.state
            return {
                type,
                label: formatSquareDimensions(size),
                size,
                isSelected: type === selectedType,
                isRendered: !!result,
                isLoading: pendingTypes.has(type) || (jobState ? isJobRunning(jobState) : false),
                result,
            }
        })
    })

    readonly $selectedSize = computed(() => sortedSizes().find((s) => s.type === this.$selectedType()))
    readonly $selectedSizeDimensionLabel = computed(() => {
        const s = this.$selectedSize()
        return s ? formatSingleDimensionLabel(s.label, s.size) : ""
    })

    // Top-align the format submenu with the "Download" row it opens from. CDK already anchors
    // nested menu items this way by default; the offsetY cancels out cm-asset-download-menu's
    // own `margin-top`, which exists for its other use as a below-trigger dropdown and would
    // otherwise drop this submenu below the row.
    readonly sizeDownloadMenuPosition: ConnectedPosition[] = [
        {
            originX: "end",
            originY: "top",
            overlayX: "start",
            overlayY: "top",
            offsetX: 4,
            offsetY: -24,
        },
    ]

    readonly actions: AssetMenuAction[] = [
        {id: "rerender", label: "Regenerate", icon: "fa-rotate-reverse"},
        {id: "remove", label: "Delete", icon: "fa-trash-can", variant: "destructive"},
    ]

    onAction(actionId: string) {
        const selectedType = this.$selectedType()
        if (actionId === "rerender") this.rerender.emit(selectedType)
        else if (actionId === "remove") this.remove.emit(selectedType)
    }

    onSizeMenuClick(size: SizeEntry) {
        this.$userSelectedType.set(size.type)
    }

    onSizeRenderClick(size: SizeEntry) {
        this.$userSelectedType.set(size.type)
        this.render.emit(size.type)
    }

    onSizeCancelClick(size: SizeEntry) {
        this.$userSelectedType.set(size.type)
        this.cancel.emit(size.type)
    }

    onRenderSelected() {
        this.render.emit(this.$selectedType())
    }
    onCancelSelected() {
        this.cancel.emit(this.$selectedType())
    }
    onRetrySelected() {
        this.retry.emit(this.$selectedType())
    }
    onRemoveSelected() {
        this.remove.emit(this.$selectedType())
    }
}

function sortedSizes(): readonly {type: MaterialOutputType; size: number; label: string}[] {
    return [...FlatThumbnailOptionLabels.entries()].map(([type, {state, label}]) => ({type, size: state.size, label})).sort((a, b) => a.size - b.size)
}

function isJobRunning(state: JobState): boolean {
    return state === JobState.Init || state === JobState.Runnable || state === JobState.Running
}

const CM_PER_INCH = 2.54

// Truncate (not round) to at most one decimal, dropping a trailing ".0".
function formatAtMostOneDecimal(value: number): string {
    const truncated = Math.floor(value * 10 + 1e-9) / 10
    return Number.isInteger(truncated) ? String(truncated) : truncated.toFixed(1)
}

// Thumbnails are square; show the size in inches with the cm equivalent in brackets, e.g. "7in x 7in (17.7cm x 17.7cm)".
function formatSquareDimensions(sizeCm: number): string {
    const inches = formatAtMostOneDecimal(sizeCm / CM_PER_INCH)
    const cm = formatAtMostOneDecimal(sizeCm)
    return `${inches}in x ${inches}in (${cm}cm x ${cm}cm)`
}

function formatSingleDimensionLabel(label: string, sizeCm: number): string {
    const inMatch = label.match(/([\d.]+)\s*in/i)
    if (inMatch) {
        return `${inMatch[1]}in (${Math.round(sizeCm)}cm)`
    }
    const cmMatch = label.match(/([\d.]+)\s*cm/i)
    if (cmMatch) {
        const inches = sizeCm / CM_PER_INCH
        return `${cmMatch[1]}cm (${inches.toFixed(1)}in)`
    }
    return label
}
